/**
 * GAP-BACKEND-ADMIN-002 — ArPresentation CRUD + Share.
 *
 *   GET    /api/v1/ArPresentation?productId=   -> paged
 *   GET    /api/v1/ArPresentation/{id}          -> single
 *   POST   /api/v1/ArPresentation               -> create
 *   PUT    /api/v1/ArPresentation               -> update
 *   DELETE /api/v1/ArPresentation/{id}          -> soft-delete
 *   PATCH  /api/v1/ArPresentation/{id}/Audience -> Share modal
 *
 * Hand-authored to mirror swagger-codegen output (Configuration + BASE_PATH +
 * Bearer + x-tenant-id headers). Same shape as product.service.ts.
 */
import { Inject, Injectable, Optional } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from '../../../../../environments/environment';
import { Configuration } from '../../../../configuration';
import { CustomHttpUrlEncodingCodec } from '../../../../encoder';
import { BASE_PATH } from '../../../../variables';
import { BooleanWebApiResponse } from '../../../common/models/booleanWebApiResponse';
import { AudienceDto } from '../../product/model/audienceDto';
import { ArPresentationDto } from '../model/arPresentationDto';
import { ArPresentationDtoListWebApiPagedResponse } from '../model/arPresentationDtoListWebApiPagedResponse';
import { ArPresentationDtoWebApiResponse } from '../model/arPresentationDtoWebApiResponse';

export class ArPresentationCreateCommand {
    details?: ArPresentationDto;
}

export class ArPresentationUpdateCommand {
    details?: ArPresentationDto;
}

@Injectable()
export class ArPresentationService {
    public defaultHeaders = new HttpHeaders();
    public configuration = new Configuration();

    // Co-located with Product service (lives in Product.Api).
    protected basePath = environment.api.products;

    constructor(
        protected httpClient: HttpClient,
        @Optional() @Inject(BASE_PATH) basePath: string,
        @Optional() configuration: Configuration
    ) {
        if (basePath) {
            this.basePath = basePath;
        }
        if (configuration) {
            this.configuration = configuration;
            this.basePath = basePath || configuration.basePath || this.basePath;
        }
    }

    private headers(xTenantId: string, contentType?: string): HttpHeaders {
        let headers = this.defaultHeaders.set('x-tenant-id', String(xTenantId));
        if (this.configuration.accessToken) {
            const accessToken = typeof this.configuration.accessToken === 'function'
                ? this.configuration.accessToken()
                : this.configuration.accessToken;
            headers = headers.set('Authorization', 'Bearer ' + accessToken);
        }
        const accept = this.configuration.selectHeaderAccept(['application/json', 'text/json', 'text/plain']);
        if (accept !== undefined) {
            headers = headers.set('Accept', accept);
        }
        if (contentType) {
            headers = headers.set('Content-Type', contentType);
        }
        return headers;
    }

    public arPresentationGet(
        xTenantId: string,
        opts: {
            productId?: string;
            orderBy?: Array<string>;
            isOrderByDescending?: boolean;
            searchText?: string;
            pageNumber?: number;
            pageSize?: number;
        } = {}
    ): Observable<ArPresentationDtoListWebApiPagedResponse> {
        let qp = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
        if (opts.productId) { qp = qp.set('ProductId', opts.productId); }
        if (opts.orderBy) { opts.orderBy.forEach((el) => qp = qp.append('OrderBy', el)); }
        if (opts.isOrderByDescending !== undefined) { qp = qp.set('IsOrderByDescending', String(opts.isOrderByDescending)); }
        if (opts.searchText) { qp = qp.set('SearchText', opts.searchText); }
        if (opts.pageNumber !== undefined) { qp = qp.set('PageNumber', String(opts.pageNumber)); }
        if (opts.pageSize !== undefined) { qp = qp.set('PageSize', String(opts.pageSize)); }

        return this.httpClient.request<ArPresentationDtoListWebApiPagedResponse>(
            'get',
            `${this.basePath}/ArPresentation`,
            { params: qp, withCredentials: this.configuration.withCredentials, headers: this.headers(xTenantId) }
        );
    }

    public arPresentationGetById(
        id: string,
        xTenantId: string
    ): Observable<ArPresentationDtoWebApiResponse> {
        return this.httpClient.request<ArPresentationDtoWebApiResponse>(
            'get',
            `${this.basePath}/ArPresentation/${encodeURIComponent(id)}`,
            { withCredentials: this.configuration.withCredentials, headers: this.headers(xTenantId) }
        );
    }

    public arPresentationCreate(
        xTenantId: string,
        body: ArPresentationCreateCommand
    ): Observable<ArPresentationDtoWebApiResponse> {
        return this.httpClient.request<ArPresentationDtoWebApiResponse>(
            'post',
            `${this.basePath}/ArPresentation`,
            { body, withCredentials: this.configuration.withCredentials, headers: this.headers(xTenantId, 'application/json') }
        );
    }

    public arPresentationUpdate(
        xTenantId: string,
        body: ArPresentationUpdateCommand
    ): Observable<ArPresentationDtoWebApiResponse> {
        return this.httpClient.request<ArPresentationDtoWebApiResponse>(
            'put',
            `${this.basePath}/ArPresentation`,
            { body, withCredentials: this.configuration.withCredentials, headers: this.headers(xTenantId, 'application/json') }
        );
    }

    public arPresentationDelete(
        id: string,
        xTenantId: string
    ): Observable<BooleanWebApiResponse> {
        return this.httpClient.request<BooleanWebApiResponse>(
            'delete',
            `${this.basePath}/ArPresentation/${encodeURIComponent(id)}`,
            { withCredentials: this.configuration.withCredentials, headers: this.headers(xTenantId) }
        );
    }

    public arPresentationSetAudience(
        id: string,
        xTenantId: string,
        audience: AudienceDto
    ): Observable<ArPresentationDtoWebApiResponse> {
        return this.httpClient.request<ArPresentationDtoWebApiResponse>(
            'patch',
            `${this.basePath}/ArPresentation/${encodeURIComponent(id)}/Audience`,
            { body: audience, withCredentials: this.configuration.withCredentials, headers: this.headers(xTenantId, 'application/json') }
        );
    }
}
